import React, { useState } from 'react'
import { Form, Modal as BootstrapModal } from 'react-bootstrap'
import classNames from 'classnames'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { Modal } from '../../../components/Modal/Modal'
import { Button } from '../../../components/Button/Button'
import { FormFeedback } from '../../../components/Form/FormFeedback'

import { useGetUserByIdQuery } from '../../../store/user/user'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

import { type ZocksDocumentUploadQueueItem } from '../hooks'
import { MAX_CONCURRENT_FILE_READS, runWithConcurrencyLimit, truncateFilenameMiddle, validatePdfPassword } from './utils'

const _INCORRECT_PASSWORD_ERROR = "That password didn't work. Check it and try again."

interface PdfPasswordModalItem {
  id: string
  file: File
  password: string
  error: string | null
  unlocked: boolean
}

interface PdfPasswordModalProps {
  initialItems: ZocksDocumentUploadQueueItem[]
  initialActiveItemId: string | null
  onPasswordProvided: (itemId: string, password: string) => Promise<void>
  onClose: () => void
}

export const PdfPasswordModal: React.FC<PdfPasswordModalProps> = ({ initialItems, initialActiveItemId, onPasswordProvided, onClose }) => {
  const dispatch = useAppDispatch()

  const { tracked, trackingClient } = useTracking()

  const [items, setItems] = useState<PdfPasswordModalItem[]>(() =>
    initialItems.map((item) => ({ id: item.id, file: item.file, password: '', error: null, unlocked: false })),
  )

  const [activeIndex, setActiveIndex] = useState<number>(() => (initialActiveItemId ? initialItems.findIndex((item) => item.id === initialActiveItemId) : 0))
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [isUnlocking, setIsUnlocking] = useState<boolean>(false)

  const activeItem = items[activeIndex]

  const updateItem = (itemId: string, patch: Partial<PdfPasswordModalItem>) => {
    setItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, ...patch } : item)))
  }

  const handleNavigate = (nextIndex: number) => {
    setActiveIndex(nextIndex)
    setShowPassword(false)
  }

  const _handleSkip = () => {
    if (activeIndex < items.length - 1) {
      handleNavigate(activeIndex + 1)
    } else {
      onClose()
    }
  }

  const handleSkip = tracked(_handleSkip, { eventName: TrackEvent.LOCKED_DOCUMENT_SKIP_CLICKED })

  const _handleUnlock = async () => {
    const { id: activeItemId, file, password } = activeItem

    setIsUnlocking(true)
    const success = await validatePdfPassword(file, password)
    setIsUnlocking(false)

    if (!success) {
      updateItem(activeItemId, { error: _INCORRECT_PASSWORD_ERROR })
      return
    }

    updateItem(activeItemId, { unlocked: true })

    trackingClient.track({ eventName: TrackEvent.DOCUMENT_UNLOCKED })
    dispatch(addToast({ variant: 'success', title: 'Document unlocked', delay: 1500 }))

    void onPasswordProvided(activeItemId, password)

    const remainingLocked = items.some((item) => !item.unlocked && item.id !== activeItemId)
    if (!remainingLocked) {
      onClose()
    }
  }

  const handleUnlock = tracked(_handleUnlock, { eventName: TrackEvent.LOCKED_DOCUMENT_UNLOCK_CLICKED })

  const _handleUnlockAll = async () => {
    const { password } = activeItem

    const lockedItems = items.filter((item) => !item.unlocked)

    setIsUnlocking(true)
    const results = await runWithConcurrencyLimit(lockedItems, MAX_CONCURRENT_FILE_READS, (item) => validatePdfPassword(item.file, password))
    setIsUnlocking(false)

    const successfulItems = lockedItems.filter((_, index) => results[index])
    const failedItems = lockedItems.filter((_, index) => !results[index])

    successfulItems.forEach((item) => {
      updateItem(item.id, { password, error: null, unlocked: true })

      trackingClient.track({ eventName: TrackEvent.DOCUMENT_UNLOCKED })
    })

    failedItems.forEach((item) => {
      updateItem(item.id, { password, error: _INCORRECT_PASSWORD_ERROR })
    })

    void runWithConcurrencyLimit(successfulItems, MAX_CONCURRENT_FILE_READS, (item) => onPasswordProvided(item.id, password))

    if (successfulItems.length) {
      const title = successfulItems.length === 1 ? 'Document unlocked' : `${successfulItems.length} Documents unlocked`
      dispatch(addToast({ variant: 'success', title, delay: 1500 }))
    }

    if (!failedItems.length) {
      onClose()
    }
  }

  const handleUnlockAll = tracked(_handleUnlockAll, { eventName: TrackEvent.LOCKED_DOCUMENT_APPLY_TO_ALL_CLICKED })

  return (
    <Modal
      show
      onHide={onClose}
      centered
      headerBodySeparator={true}
      header={<BootstrapModal.Title>File is password-protected</BootstrapModal.Title>}
      body={
        <div className='d-flex flex-column gap-4'>
          {items.length > 1 ? (
            <div className='d-flex align-items-center gap-3'>
              <span className='paragraph-medium'>
                {activeIndex + 1} of {items.length} documents
              </span>
              <i
                className={classNames('bi bi-chevron-left', activeIndex === 0 ? 'zocks-text-gray-400' : 'cursor-pointer')}
                onClick={() => (activeIndex > 0 ? handleNavigate(activeIndex - 1) : undefined)}
              />
              <i
                className={classNames('bi bi-chevron-right', activeIndex === items.length - 1 ? 'zocks-text-gray-400' : 'cursor-pointer')}
                onClick={() => (activeIndex < items.length - 1 ? handleNavigate(activeIndex + 1) : undefined)}
              />
            </div>
          ) : null}
          <div className='d-flex flex-column zocks-gap-0.5'>
            <span className='paragraph-medium'>Enter the password to unlock it. We store the password securely for your account's retention period.</span>
            <span className='bold'>{truncateFilenameMiddle(activeItem.file.name)}</span>
            <div className='position-relative'>
              <Form.Control
                type={showPassword && !activeItem.unlocked ? 'text' : 'password'}
                className={activeItem.unlocked ? 'zocks-pe-5.0' : 'pe-5'}
                placeholder='Password'
                aria-label='Password'
                value={activeItem.password}
                onChange={(e) => updateItem(activeItem.id, { password: e.target.value, error: null })}
                disabled={activeItem.unlocked}
                isInvalid={!!activeItem.error}
                autoComplete='new-password'
              />
              {activeItem.unlocked ? (
                <span className='position-absolute top-50 end-0 translate-middle-y me-2 d-flex align-items-center gap-1 zocks-text-success'>
                  <i className='bi bi-unlock' />
                  Unlocked
                </span>
              ) : (
                <i
                  className={classNames(
                    showPassword ? 'bi bi-eye-slash' : 'bi bi-eye',
                    'position-absolute top-50 end-0 translate-middle-y me-2 cursor-pointer zocks-text-gray-700',
                  )}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword(!showPassword)}
                />
              )}
            </div>
            {activeItem.error ? <FormFeedback className='d-flex justify-content-start mt-1'>{activeItem.error}</FormFeedback> : null}
          </div>
          <DocumentSharingEnabledForUserBanner />
        </div>
      }
      footer={
        <div className='d-flex align-items-center justify-content-end w-100 gap-3'>
          {activeItem.unlocked ? (
            <>
              <Button variant='primaryGhost' onClick={handleUnlockAll} loading={isUnlocking}>
                Apply to all
              </Button>
              {activeIndex < items.length - 1 ? (
                <Button variant='primary' onClick={() => handleNavigate(activeIndex + 1)}>
                  Next
                </Button>
              ) : null}
            </>
          ) : (
            <>
              {items.length > 1 ? (
                <Button variant='grayGhost' onClick={handleSkip}>
                  Skip
                </Button>
              ) : null}
              <Button variant='primary' onClick={handleUnlock} loading={isUnlocking} disabled={!activeItem.password}>
                {isUnlocking ? 'Unlocking..' : 'Unlock document'}
              </Button>
            </>
          )}
        </div>
      }
    />
  )
}

const DocumentSharingEnabledForUserBanner = () => {
  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id

  const { data: userResponse } = useGetUserByIdQuery({ accountId, userId: currentUserId })
  const documentSharingEnabled = !!userResponse?.data.effectiveSettings?.documentSharingEnabled

  if (!documentSharingEnabled) {
    return null
  }

  return (
    <div className='d-flex align-items-center gap-2 zocks-bg-color-gray-100 border zocks-border-color-gray-200 zocks-p-0.75 rounded-3'>
      <i className='bi bi-info-circle fs-6 zocks-text-gray-600' />
      <div className='d-flex flex-column paragraph-small'>
        <span>Team members can view this file based on your sharing settings.</span>
        <a href='/dashboard/account/profile-settings/collaboration' className='bold zocks-text-gray-900 text-decoration-underline align-self-start'>
          Review settings
        </a>
      </div>
    </div>
  )
}
